import { Helmet } from '@dr.pogodin/react-helmet';
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-typed-form';
import { Api, useApiCallable, useApiFormSubmit } from 'src/api';
import { getApiErrorMessage } from 'src/api/hub-api-request';
import {
  Button,
  EmptyState,
  PageError,
  PageLoading,
  PageWrap,
  Stack,
} from 'src/components';
import { Color } from 'src/constants';
import { Model } from 'src/models';
import { toDayInput } from 'src/util';

import BackToProjects from '../components/BackToProjects';
import DeleteServiceOrderModal from '../components/DeleteServiceOrderModal';
import EditStageModal from '../components/EditStageModal';
import SaveBar from '../components/SaveBar';
import ServiceOrderDetailsCard from '../components/ServiceOrderDetailsCard';
import ServiceOrderPageHeader from '../components/ServiceOrderPageHeader';
import ServiceOrderStagesCard from '../components/ServiceOrderStagesCard';
import WhereItIsUpToCard from '../components/WhereItIsUpToCard';
import {
  type ServiceOrderFormValues,
  type StageFormValues,
  buildServiceOrderWriteBody,
  emptyStage,
} from '../service-order-form';

export default function ServiceOrderPage() {
  const { serviceOrderId = '' } = useParams();
  const api =
    Api.CompanyProjectSummary.useReadCollectionProjectSummaryAsAgency();

  if (api.error) return <PageError {...api} />;
  const summaries = api.data?.['hydra:member'];
  if (!summaries) return <PageLoading />;

  const found = Model.ServiceOrder.findServiceOrder(summaries, serviceOrderId);
  if (!found) {
    return (
      <PageWrap background={Color.neutral5} limitWidth="small">
        <Stack gap={1}>
          <BackToProjects />
          <EmptyState
            title="Service order not found"
            subtitle="It may have been deleted, or moved out of the projects you can see."
          />
        </Stack>
      </PageWrap>
    );
  }

  return <ServiceOrderEdit found={found} invalidate={api.invalidate} />;
}

function ServiceOrderEdit({
  found,
  invalidate,
}: {
  found: Model.ServiceOrder.ServiceOrderWithContext;
  invalidate: () => unknown;
}) {
  const { serviceOrder, company, project } = found;
  const navigate = useNavigate();
  const callApi = useApiCallable();

  const submit = useApiFormSubmit('PUT', `/service-orders/${serviceOrder.id}`, {
    onSuccess: () => {
      invalidate();
      navigate('/projects');
    },
  });

  const pristine: ServiceOrderFormValues = {
    name: serviceOrder.name,
    referenceNumber: serviceOrder.referenceNumber,
    budget: serviceOrder.budget === null ? null : String(serviceOrder.budget),
    dayLimit: serviceOrder.dayLimit,
    stages: serviceOrder.stages.map((stage, index) => ({
      key: String(index),
      name: stage.name,
      startDate: toDayInput(stage.startDate),
      estimatedFinishDate: toDayInput(stage.estimatedFinishDate),
      finishDate: toDayInput(stage.finishDate),
    })),
  };

  const form = useForm<ServiceOrderFormValues>({
    defaultValues: pristine,
    onSubmit: (values, formObject) => {
      const companyIri = company['@id'];
      const projectIri = project ? `/projects/${project.id}` : null;
      return submit(
        buildServiceOrderWriteBody(values, companyIri, projectIri),
        formObject
      );
    },
  });

  // There is no server value to compare against, so "has changes" is judged
  // against the values the screen opened with.
  const isDirty = React.useMemo(
    () => JSON.stringify(form.values) !== JSON.stringify(pristine),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form.values]
  );

  const [editingStage, setEditingStage] = React.useState<number | null>(null);
  const [confirmingDelete, setConfirmingDelete] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);

  const commitStage = (stage: StageFormValues) => {
    if (editingStage === null) return;
    const stages = [...form.values.stages];
    stages[editingStage] = stage;
    form.getField('stages').handleValueChange(stages);
    setEditingStage(null);
  };

  const removeStage = () => {
    if (editingStage === null) return;
    const stages = [...form.values.stages];
    stages.splice(editingStage, 1);
    form.getField('stages').handleValueChange(stages);
    setEditingStage(null);
  };

  const addStage = () => {
    form
      .getField('stages')
      .handleValueChange([...form.values.stages, emptyStage()]);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await callApi(`/service-orders/${serviceOrder.id}`, {
        method: 'DELETE',
      });
      invalidate();
      navigate('/projects');
    } catch (err) {
      // The API refuses while the order is in use; say what it said rather
      // than leaving the Delete button stuck on.
      alert(getApiErrorMessage(err));
    } finally {
      setDeleting(false);
    }
  };

  const meta = [company.name, project?.name].filter(Boolean).join(' · ');

  return (
    <PageWrap background={Color.neutral5} limitWidth="small">
      <Helmet>
        <title>{serviceOrder.name}</title>
      </Helmet>

      <Stack gap={1}>
        <BackToProjects />
        <ServiceOrderPageHeader
          title={serviceOrder.name}
          reference={serviceOrder.referenceNumber}
          meta={meta}
        />
        <WhereItIsUpToCard
          stages={serviceOrder.stages}
          totalMinutesLogged={serviceOrder.totalMinutesLogged}
        />
        <ServiceOrderDetailsCard
          form={form}
          companyName={company.name}
          projectName={project?.name ?? null}
        />
        <ServiceOrderStagesCard
          stages={form.values.stages}
          onAddStage={addStage}
          onEditStage={setEditingStage}
          footnote="The stage in flight is the first with no finish date."
        />
      </Stack>

      <SaveBar
        left={
          <Button
            label="Delete service order"
            variant="text"
            onClick={() => setConfirmingDelete(true)}
          />
        }
      >
        <Button
          label="Cancel"
          variant="text"
          colorScheme="black"
          onClick={() => navigate('/projects')}
        />
        <Button
          label="Save changes"
          onClick={form.handleSubmit}
          disabled={!isDirty || form.isLoading}
        />
      </SaveBar>

      {editingStage !== null && (
        <EditStageModal
          stage={form.values.stages[editingStage]}
          onSave={commitStage}
          onRemove={removeStage}
          onClose={() => setEditingStage(null)}
        />
      )}

      {confirmingDelete && (
        <DeleteServiceOrderModal
          name={serviceOrder.name}
          numStages={serviceOrder.stages.length}
          totalMinutesLogged={serviceOrder.totalMinutesLogged}
          onConfirm={confirmDelete}
          busy={deleting}
          onClose={() => setConfirmingDelete(false)}
        />
      )}
    </PageWrap>
  );
}
